<script setup>
  import {computed, reactive, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentTab from "@components/Tab/Tab.vue";
  import ComponentTabs from "@components/Tab/Tabs.vue";
  import {medicationMeal} from "@enums/medicationEnums.js";
  import {router, usePage} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import resetState from "@utils/Helpers/ResetState.js";
  import {each, map} from "es-toolkit/compat";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "MedicationComponentAthinaImportExport",
  });

  const props = defineProps({
    hideImport: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["isOpen"]);

  const page = usePage();

  const refAthinaImportExportDialog = ref(null);
  const refImportPayloadTextarea = ref(null);
  const refExportPayloadTextarea = ref(null);

  const initialState = {
    activeTab: props.hideImport ? "Export" : "Import",
    step: "start",
    importLog: [],
    activeImport: null,
    exportPayload: null,
  };
  const state = reactive({...initialState});

  const label = computed(() => (props.hideImport ? "ATHINA Export" : "ATHINA Import / Export"));

  const tabChanged = (newActiveTab) => {
    state.activeTab = newActiveTab;
  };

  const openDialog = () => {
    refAthinaImportExportDialog.value.open();
    emit("isOpen", true);
  };

  const closeDialog = () => {
    if (refAthinaImportExportDialog.value) refAthinaImportExportDialog.value.close();
    emit("isOpen", false);
    reset();
  };

  const reset = () => {
    if (state.activeImport) {
      state.activeImport.cancel();
    }

    for (const [key, value] of Object.entries(initialState)) {
      state[key] = resetState(value);
    }
  };

  const useWorkaround = () => {
    state.step = "navigatorClipboardWorkaround";
    setTimeout(() => {
      refImportPayloadTextarea.value.focus();
    }, 50);
  };

  const importFromAthina = () => {
    const isFirefox = navigator.userAgent.includes("Firefox");

    if (!isFirefox && typeof navigator.clipboard.readText === "function") {
      navigator.clipboard
        .readText()
        .then((text) => performImport(text))
        .catch(() => useWorkaround());
    } else {
      useWorkaround();
    }
  };

  const performImport = (text) => {
    if (text === null || text === "") {
      state.step = "start";
      return;
    }

    state.step = "loading";

    if (state.activeImport) {
      state.activeImport.cancel();
    }

    const importRequest = useAuthHttp({data: text});
    state.activeImport = importRequest;

    importRequest
      .post(route("api.records.athina-import", {patient: page.props.patient.id, record: page.props.record.id}))
      .then((response) => {
        state.importLog = response?.log;
        state.step = "success";
        router.reload();
      })
      .catch(() => {
        state.step = "error";
      });
  };

  const exportToAthina = () => {
    state.exportPayload = "";
    let rows = [];

    each(page.props.medications?.data, (medication) => {
      if (rows.length >= 20) return false;

      if (
        medication.additional &&
        medication.additional.discontinued &&
        medication.additional.discontinued_by === "doctor"
      ) {
        return true;
      }

      let row = [
        medication.selected_package_pzn,
        medication.fam,
        map(medication.substances, "name").join(", "),
        map(medication.substances, "strength").join(", "),
        medication.form,
        medication.usage === 0 ? "x" : "",
        medication.usage === 2 ? "x" : "",
        // the formatted values keep the fractions of the MediCheck ("½" instead of "0.5")
        // and write decimals the german way ("0,5"), so that excel reads them as numbers
        medication.dosage.morning_formatted,
        medication.dosage.noon_formatted,
        medication.dosage.evening_formatted,
        medication.dosage.night_formatted,
        medication.dosage.selected_unit,
      ];

      let dosageInfos = [];
      switch (medication.dosage.meal) {
        case "A004": {
          dosageInfos.push(medicationMeal.A004);
          break;
        }
        case "A011": {
          dosageInfos.push(medicationMeal.A011);
          break;
        }
        case "A007": {
          dosageInfos.push(medicationMeal.A007);
          break;
        }
        case "A022": {
          dosageInfos.push(medicationMeal.A022);
          break;
        }
      }

      if (medication.dosage.info) {
        dosageInfos.push(medication.dosage.info);
      }

      if (medication.dosage.dosagealternative && medication.dosage.dosagealternative.text) {
        dosageInfos.push(medication.dosage.dosagealternative.text);
      }

      row = [
        ...row,
        dosageInfos.join(" "),
        map(medication.indications, (indication) => {
          return indication.name || indication.name_icdc;
        }).join(", "),
      ];

      rows.push(row.join("\t"));
    });

    state.exportPayload = rows.join("\n");

    setTimeout(() => {
      refExportPayloadTextarea.value.select();
      refExportPayloadTextarea.value.setSelectionRange(0, 99_999);
      navigator.clipboard.writeText(state.exportPayload);

      setTimeout(() => {
        state.exportPayload = null;
      }, 700);
    }, 300);
  };
</script>

<template>
  <div>
    <ComponentDialog ref="refAthinaImportExportDialog" dialog-width="medium" :test-id="getTestId('athina')">
      <template #content>
        <ComponentTabs :active-tab="state.activeTab" @click="tabChanged">
          <ComponentTab v-if="!hideImport" title="Import" :test-id="getTestId('athina-dialog-import')">
            <div>
              <h3 class="py-3 text-lg leading-6 font-medium text-gray-700">ATHINA Import</h3>

              <div v-if="state.step === 'start'" class="flex flex-col gap-3">
                <p>
                  Importiert die aktuelle Medikation aus dem
                  <!-- prettier-ignore -->
                  <span><strong><span style="color: red">A</span>THINA</strong>-Bogen.</span>
                </p>

                <p>
                  Öffnen Sie die
                  <!-- prettier-ignore -->
                  <strong><span style="color: red">A</span>THINA</strong>
                  Excel-Datei und markieren Sie dort die zu importierenden Zeilen in der Medikation. Wählen Sie dann in
                  Excel „Kopieren“ bzw. drücken Sie
                  <code>[Strg]+[c]</code>
                  zum Kopieren in die Zwischenablage.
                </p>

                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABMYAAACuCAMAAADqO/HaAAABuVBMVEWVs9fGxsars7vc5vEAAAAhc0b///88AACVs5oAAFZqs9cAADFUmtdUKAB6s7uVs7mrs4bGxo08gNchZblEAEuNxsYAKHmbm5sASJqqcC2VgFYtcKqAs9dPAE9gmbshAABwqsaAZTGVmnmTZCpqSACrs6FPjcarmWnGjU+Ts7urf0t6RwDGxqqNTwAnZKEAT41gKCrGqnBEf7sAKGkAR4ZwLS0AACtEAABPAACqxsYAAE8AAEsALXAnAEtYpPEtAC2dXABQAC5wLQArAABgKAAmACpUZTE8gLknKGktAE9UmrmVs+MtLXBEACqd5vFvbi275/aAs7ncxYdDKGpUZVYAR0tgZCq5s9l9s5DcpGCDg1QAKDHc5s8AXKxxUAB8xfHc5qx8NAD/6ebMwdeoxowAAGA8ADHd//Mygc+Nxo2Ts4Z1mnloKk29gTbh//+o2viAmrloq5UnZIZPLXAnZGkyAGAjKB3M8v7///IxdJAANIcSKFYAT08/Qi1gRwA8KADdzddzracqQYYASHktcHApTFEAADZYAAD///iVx++ES016f0tUSADM2t6os9dYNIdYAGAyADZPTy2XLL+8AAAYjklEQVR42uydB6+rNhuAkfTeffN9LUgpQpEiIv5GNbv3nlrde++99/jNxThPnTiE+GLHh9MYtQnD136N4TnvspMNb5IF2iSLvp2AxHIGNcgtu+fq0q3q1VLunFllZbfc5Ut5IzJfuIo6iXIS74FJj79I6M4kjCWMVdJuV64eqvr/M5G8otwQxu6czZ+hXEQ80Y+EsYSxhLHTwRjYcXv9L798zyxvhjEG7srKBYvzN5aSB8MY/UgYSxhLGDtBjB02KsGTyKBRifF57Z6ZXWd443NEP2g3y8DsqWAsYSxhLPnG4NgudXywg/EZSGurE8YSxhLGklF5RGNswPj019roxHA/aNUulzCWMJbGMWHMxdXuY3yitYXEWBHVN4aQSDe0rZZF+AfDXRiuXb507Xr4HieMBZc4Ycw/EihhXe3uWpuPMZuHd/GDx5y3fzzGGgq5b27pMtD7lDCWMJYw5oAnGe9qZ3PX2qaZNwbGzFvvhTFuX3iMXbmq/v+PYCxhLGHMwYXuiCfxcbVjBQbCU6PevYM+tAIcHwdj6u1vX947lOLDd6eptZet87VIub4NxabOmx8RY/MFDe4TTqttGmPgjGso5FmtvnMO274/JvOnrMJUlDAWQeKEMefEB/AUxKikHC37Y4wI5FlhTDbefikUAfiGIpvnV0spdNn5ov23uamH+3Eko5IG9wmn9y2MWdcqnXfDoeo7GLMLJ4xFkjhhDBd6XN/Y/2dFljVlTUkv1z3aYuMfqvDXxmwKaITYdOi0mOuV7l5RaaDj4D+ONpY1ktPgPuEUouwO2NcqrcBxqPveXzhhLIrECWOuLnQ/LaYfO/8HoAEwljWk504NYxWU6MOYpY3WupojYayWggb3CVc7YEyjrxjGWH0IY+9cbLdPnzynGKNrAcu7S5ww5pH4UAbPGxMpm/nCQWurS9f0XNcp7lExVmKAWRhT+8qgbPKBmxwOY8qYpMF9win/Gfv8b1/reCsFh2Bst/AQxu595YXpaWOY3j0bo5swdiyMobUU/pFKf98YmBisD5kZ2vDGovsU9+AJFzbGlJ747B6Mrf9h7u/gd/KN5Uia7xNOu+839xHYXKu13qsPwVh/4SGMPTk9jBHPPSNtLGGsMxlKZ4wZv3j7X8CECzARFjs+cy/dp7hPYEM3na5PBaN099B5w6j8dqoYM/HcWjsSOVR41vulHY7eG9XV1Xb7VEOkt2dLGOsMG+LnOgj+cPe5iIgxMBEEY3DMSWvjr3+wKe7RNxz808QYt87cYQ7HYezetyfoG7PjuWtQc4jh3J62w9GXBwK17FNNNaBvJIxljRQbbhBRGBMZxFhXLOxkJDDhjp3IC/XgQ0tzKtOcykPx3PUZDnEmZrsRjaFALfubYemhUE7CGPqr9lfozwGjUhd3TGjoMR/EERPO2PFbMSOv3I3PhLGEseF4rj6h3iAOR2KsBmOm1gFPdjIqiZ87Ygy70tHF74ix0NipnPPQmrRs4jnBmHLwT9KotOK5+jvjEKPSxtiBqC77VKPR5o6x8DPkdJ+UtO0HlEYJyb3uaiU5vpE+Cey2+l38xM8NxvTFIYxRb9y8MbATTmt7d7WU0m+Ke6R1LiaHsc1ssqP3QaS/TlGJhFR8sDM8GSUmX27X4FrRQDwXFz/hXePitzE2FKhlf6vWMuQ41sQfKinVdy9EuECaZa4/GM2RYysMhL47gxjra6s34YL4uTvGsjosxsxcSX/s+E8198KxO5mo75xiDJnMjvX0x8HYtevuGEOKRgr+sFODB8bCR3XjjONqWSoE51mNLjWEMa7pD/NvkLq4MRmV2oSuOqwP9rU10fXGQs+VzOrQGCtDGpXod/l5xRgPV3SM/fb2ry+88/xGnc8u25gUM80XjhirpdAUmy+oYQoYI4wbYRwxqer5n8X/Z1oXJFGhMxtLrQ3eqSTqaCc6XVDmf4oId4xkEMwzdxmVBUiPSytVAgl07VpXJkmFtiaJMTNX0n8qt6t8lHLqRx0cY422ERidRrrHqTKjZ6/6cGDpiMgYwxCzspvWQt6jn34tmZU0RdFRCRefvPDQCzoHljfxGblzRpDdEWPavdJRzNRwWnMqWd6lKer5Uy0WNMY0oNToFXBGXdCjyo3rPjS2SAbhaXDehOdG14+HcVsCatdtkZSi24q8bKLjJB7mSsbFGC6IIhu1SqyIn7frPTWCjM7aP1FLzujZyUQHlo6IjTEl5m5206bzmjfBSpqi6BiMtQSzMfbw7Itv8Ic4+8ZytaMoRg0nibG6xUVeXXljqX1k//qUPuaPZHtsBrKR3MYYySAaiTcoY2N8Y0BtWwJqB2OsZ5c1sTHmOInHYa4kpQImXECxPIuMMeLpzXYi9xtffFM0JlHIjsIfWjoCdSQqxuzsJgtjIoZmXKLoOG3s24cwKhGiEnHHGKYtFKOGk8TYavnZy9dXtz8m+RbG5NklaJFiEGP48UZoY7wEuv6tVAkkoHYbYwG1sTrs71QyV9J9TfxQPjRSCuMmXNAVM6M6a649VtY/zOYLezmJ+gDGrISiuBizs5uMkOZNsDBG0REYUxy7ePH5v7eFaMAYjHLBmBLc1DBFjGFuHWMcaeGnW9s79/HatjMRvno9RLXydPQalRpbJIOM8I3VpQ58qvqtVAkkoHbaAmPxfWN+k3jEMV9t/FTzhlcqKsZImbRj70X17LLIGD07mejA0hFn4xuzs5sQEu1ISWYlTVHUO1IJhFbLURjrtpIa3DHmH4C2AcVN40IcjPH4qw8LY+rcg0zl33DxG4zpAA/JIDceqSTVRNffkyqhpKJ21ZbBmBWp1IWkHIcx0lBDTOKxYe7vG3Ocat7Q++h5Y40UKNVmJULtKODZsJOJhpeOOKtIpZXdhJDqu0Sy7aQpip7rLH7/ALS1xcdYqEzGETZlJt5tiZ16OwZjwbPf67AYw5h1j1SGzxuLn2QUP28schb/X3r/oekum0gAWqcOPIjDcDtGS7yZ31YgtPuD+ig3SxHrnR7GPLP4vdvaxRgaIz/ywO0dwBhpqOHyxlx/L9LDmI2fNxY/ySh2Fn9UjGUPPd+5x56fqDZGABqNEy+4HaMl3sxvK2wblZulBqLQaU6lvWjf9o88mNsb/gfewmCsCLeqq+fyjxG3NKdSc+zet7+drlFJAJpMdHyg2zFaaxlMC2ObpQ4ua5EwhjZGNBUvKbc3JsY8XfzeN2/yRmXCGGvx//rCdH1jJgDdvT6sdWHHaA9hzJQiCh1a4oQx95/sYGuKEJFKtlPGWMJY9tvzE3bxmwA0L5PkPTHaf9g7A84IYigIQygKtHBAXd0f7P+H2lTmFukavOw4+wVFBEXus3lv3swMY89xxf0ptwsNxgSudRjTOc9vzMiLdAQXpY9KjHoWNQDc6nBrw/s1b9RjNKDH/PROE6we7RRjvbXb/+xPuV1oMKYSv40xP3nI/IpR0FoZxgwvfgk90hjTj163vVYR6V+MQAiQfZCcSjBWH7drJQ9pgs3oVG5YrHR1df4/CT1sfC7HWPpqBUKApJxcjzEwBsYqkofWY0z1uIIR8gDGJCn6x/RCUqKxlcBYaQhQ/yIGY2AsgDEzeUjLf1QWmFP7tTthLP+obM+kxyPTC0mJxtbZGKsPAdpQDcbAWABjWn6S0fpIEQNj3grXxo5MLyQlGlvVVysQAvQAY2AsibGAbuwWEFz0V+WnHr1nYWxmeiEp0dgKYKwuBIivMTAWwJjhIGGMkLekObWB2TjGDkwvJCUaW+djrDoEiNoYGAthbO4gYSYZNb/m5Z8zHrOvhrGJ6YWkRGOrGmOBECA6lWAshDE5SPhJRgUzlfXn1NUMYIzr39+ielOCMTAWEVxUOlzc/NpYdRq4b+gDxsr9OdRtBWNgbD3GCh0kml/zmpwLxPKCsfwCY2As1qn8/tKP36+N1aeB21MGzFSCMTAGxiZwkmHSytqYsRgNB2NgDIzt8fT+8+YkGf3F48wFFwk9GBgDY2AMjKkwZsxUDjg5Rj33AMYUhZ8cRgosMAbGwJgECA9Db9UR5ZtYJ+wQPy6JMTAGxsDYZjh2r+jw5THW2gUxBsbAGBhrbY1Q4XyM/bJrBxoIBEEAhtPNHlBFsYDo/R8ygGvRhnO7w/cDyFhmfMI93i+MYQxjGOuPnpix0+2CMYxhzAcXORlrwxjGMIYxjGEMY/aIMYw5f4xhDGMYw1hpk2oZntS49qsyYcKYFNt6/8ZW6bu6SoMrZdnWZWzZpzhnKzK8ccCEqPuOjn+fMPnv4riDcf4YwxjGMIYxjGEMYxjDmD1iDGPOH2MYwxjGMIYxjGEMYxjDGMYwhjGM2SPGMOb8MYYxjGEMYxjDGMYwhjGMYQxjacMYxjCGMYxh7P68ptgjxjCGMYx92DUDD0mOKIzT8wCMRgA0tNG2m3JqOtMYGRt9IZC15lzcsJudI8s4WThJcNyRQ5YESf7j1Ff3tmoqW0lqotPX3aqZeTNvvurpV13186r6VUTLs5MwtniS/Y2z+Ox09pUk+TLW84ixiLHTdRFjEWNVrUgiPh7GqrqS2sRsLGLsP+kixiLGGDxpQ7TKFLu+JpLIkOi5wlhOVCeLp8iTWLB48pZeK8fyldvAYkw3AgTxSp+9o/WLhsRf/oANg0waaFp9UpGbJUaM+Y+IsYixiLHSoAIf06ZOSsWsr7IkX8+LL88UY8r6SLB4WjOxnAZJomlH9NDoAWONSKpVxqy0ejYWY8WPLehn9DgNriJiLGIsVBcxFjGW6+SH6aOcMPCRSBvwhgWaYHhzG9hsjBuxHyav8ZurZ2MxBtCljbT6iLGIsdN0EWNxUQkDcrgY4zwMIGOBwRh/92BMN3qEMUfvxRg+WD0WoSLujf37ETEWMRYxpleJxXuAqzLJUvGFXlQy4iCSLDAYcxtYjHGjtJE4g8WS0Xsxhn9I1L8e6UXc4g86IsYixiLGeAcfO+rPnxkq5USvv9NmleWEXSsWaFgp//LMbWAwxo3wkOB7Z1Hp6G0z+KEvWuz5Wz2+kzzpPqLxtDFmQsxFxFjQSJguxnLhi9jr8/8M57AwtqONtnewO6I3l9apzdVLIrpW386Jbi8gocOwq/grkSRl/cjdZaVbLxgLL47znwE4B+cHgDHEEHC+kohEX7oKv+m7Wy5fNXLyGONHaeJ4CCD8KWDs/PpcE2v7/pvNbP/b5dXLg3HCAGMbVuJ9//nNbPvDxaAxVrTeGW8r3caDMX9xXPgZcjGEbAwxhGAMD2awKdGLDlsVv8zduzt1jPHG9NQwhkMT6+rTm3POyg7GCWMwtv/9woOxMR1dVbr1jLHw4jg/xpxoRacYy1dZ2oigRWUQxsoaStGPDhhLFvru8jZFiX7SpT5yuhhD2Lng52g/6fDtNGCzytypgXkgUk8vDQxjgBesgZaDMSLazLZvW7223LZYWo4RYx1WuvWPsdDiOA/GzMULpCZdYixtlu/W8+4wltd8yb3oKhImHcFLd6f68KKR019UcswIn0c1xoZ6cS84UwPzwNdLA8MY8ism1921cVoz291eQLK/38zubr9tr0eJsQ4r3frHmL84LgBjTrQ4VYcYw5llMlKMYVavMosxPQ507iqnvKhEEuVijD9y6sq9YLuEVZ5eGhzGdoTjoCj25tKDMSwlgTH1facEyNhGiLEuK936x1hQcZwHYyba/wljomuMVaI/XSUeY+yTX9fz6WIMMf8TxmQgxtBLQ8MYW97G92djYNf2jxtgbH8/Sox1WOnWP8b8xXEhGDMX3/2ismhFtcq6wxiva/rR5RKdahKUD6sqPVvz5dnUszGO+Rhj3FncC9bHKn8vDeZJJRH9fMPI2raEogp2stnfa5/+EaQzBRdjOzqodOsbY6cXx3kwxsHwFj+P4U4wVqlR3dYBGOMYggokZNKPDuls/eHu6kJFHgDo4UpMv+DCxGyecHBnmV6wU+NPdu5AM2IgCAOwY3ahBYFUARro+79gNUN0RdqSbZrl+ym6KqIz98mducnXwf6/ZPvryVibmMNxpvhN8ZviP5Fo1oRdOHOFsXY4DmMYwxjGfKcSYxjDWLvtMAfn1rN1uPBdHTF2T8YwhrGjbYc5OJdnnsbuzBjGMIax422H2zDpCIxhDGMYw9h+2+E2TDoEYxjDGMYw1m47TMbyDGMYw1i/YOw1pv6MHWw7/PzJszcf8WMMY52CseVlmYy/YmxoxjDmTSXGMIYxjGEMYxjD2IDBGMYwhjGMYUwdMYYx7Y8xjGEMYxhbIuLpGWMYwxjGjL9iDGMYwxjG1BFjGNP+GMMYxjCGMYxhDGMYw9jfBGMYwxjG4ruUUkOkyRwi/5taSvP7j09jjz6Jx2iJIe7x+ivE3PfS8dtbuPnfxXUNo/1rKfVrMIYxjGEMYxjDGMYwhjF1xBjGtD/GMIYxjGEMYxjDGMYwhjGMYQxjGFNHjGFM+2MMYxjDGMYwhjGMYQxjGMMYxjCGMXXEGMa0P8YwhjGMYQxjGMMYxjCGMYxhDGMYU0eMYUz7YwxjGMMYxjCGMYxhDGPqiDGMaX+MqSPGMKb9MYYxjGEMYx/smoGH5EgUxoUAcApBiSYO0ehSGtPS0ZyZBscwABwGDAwHZ8AxcNzBAe4vvvrefL31drfVxlRnEpledpKqelWp9/WrX14luaAqttYls6rG8NjWY854sRijb/T4irG0TIvGWDroWZdoZuWMMLYuy7LmQTws22JzF0qeJR7EZNegBwpUxXT+B1Bw0cApW14jlpMt+RjLmPHkGEtLkhhheyxL/mjTYYyRFZ1IjBeNiw+wc2jb3DTB7PDUtYvHGH+CWocA3F8AxsxtU2x/aeTwa5jZ9v4RGGvRxhIPaF73WOUuYsz+g07vyW14Df0vKhtaD/tRMJYx40kx1r5zBLo3HcYYYHQiPV40/gi7dQixfyvB2GfJxuAs1vHiMLb2uC3VxBi8cl8wxhIP1ktYwJwYQ8tfwdS8PJb9cyeYZ2LXyuF1JYqZ1UMpjTxt48rUfV3oB3hJYJkuXLEsPYINFZIKSk0GxjJmPB3GUmINw5jpIB6duijG7K4xXZ3EGANsGMai8dh2xJjkJ4enVSXrUoIsnCD4FosxuC1xDZ9/EvcZ4lwGVOFBrYnNzX9lbc6oNBeMWS+xjv1HX8mZYAwOscQDMjFGYsTY9n4fIgWR7HYNMY+7H/M720sN2qWIUy8DFbyG6hvGgpFoLcEXKnApV6OftLAmA2MZM54MYwmxhmGMa9Z04uolMWa6w2NfJTHGAItOpMaLxsUH2DlAnekI/oui4eS5a5e/qaTPcJ8hrpZBcarjmtjc+eKcSrPDmO1/PnokZIQUfGDpVOn4nEVh7A0w8M96BAQqeAcE9NFAxdCJp1I8XSP2/RZjrS1Fc9P11duds2BNBsZyZzwFxhJiDcSYldSNTl10U2kJpiEYoxNzwZiAddcojEmQIXdtl7ypNJ2Oa+1+XAaFloRW51WaD8Y4d6Ra61JAwccKLMVKeKExZjppOQeFdggUvgIKBqu5lZUm6/mcOmLM5z0by5/xFBhLiDUMY7gPgXvjYKwegDFXRydS42nj8e2k7XuMrX7rq+ViDD6nMNYOxBhVmgnGmElibtzrxmyMJX3whcYYeuEtj4bCaUQc1BYNxXQ2tma0UevDXl45AK7WS3dZj1kYy5/xFBhLiDUMY3DajbKp3B5rt2uSGKO4w7IxbTy6nW0RXExQ8MvDUlarPeyXno3RZ40xtQwKXUer8yrN6YML7hhrEktyFsZbxBje3vsi7i1L3ufM7R8aChxRbtR//l7pp+QaCqdrqH3SEf30i3mLC1kv+sk5/2ZgLGfGU2EsIdYwjOEne33Z6/cWXXsRjLkQ1UefwhjFxRXpRNLZdXRrXDuK6vGrH/bB7G9oy4fXCJPFf3Bx8lm/4YBYVIF1sib4Ove8Sp/lK/6BFHCQ1icM5jPj6edIsa5f8V+/4r9+xT8rKGyPvFlfMZYl1v/t21OSBFEUhsHGf8e2vZzZ/3bGNlonIr/XRjnLGMMYxrxT6Z1KjGFscGEMYxjDGMYwhjGMYQxjGMMYxjCGMcsRYxiz+mMMYxjDGMb2kq2DsoxhDGMYw9jM5cHy4nppxjCGMYw5qTwtzRjGMIYxjC0vrlVmDGMYwxjGTnc6hRnDGMYwhrHTrYPCjGEMYxjD2MVO5QcuMIYxjGHs7DjJ7BzGMIYxjHn8FWMYwxjGBhXGMIYxjGEMYxjDGMbGj9htGMMYxjBWmLF2268Ye520PTXWpPeM5ava26TtNs6ke8byos6XtUlMGBPG+i/qfF36Ayr9alUY44zhH7I92L9OBvF/4/9e0h9NVn+MYQxjGCu/+if9PsYwhjGMYQxjGMMYxjBmOWIMY1Z/jGEMYxjDGMYwhjGMYQxjGMMYxv4RxjCGMYxh7Bp00VIsZ4w8DQAAAABJRU5ErkJggg=="
                  alt="Markierte Zeilen zum Kopieren aus ATHINA-Excel in MediCheck"
                />

                <p>Klicken Sie anschließend hier im MediCheck auf „Einfügen“.</p>
              </div>

              <div v-else-if="state.step === 'navigatorClipboardWorkaround'">
                <p class="md-body-2">
                  Drücken Sie bitte jetzt
                  <code>[Strg] + [v]</code>
                  auf Ihrer Tastatur.
                </p>
                <textarea
                  ref="refImportPayloadTextarea"
                  aria-label="Import-Zwischenablage"
                  style="height: 0; width: 0; opacity: 0"
                  @input="($event) => performImport($event.target.value)"
                />
              </div>

              <div v-else-if="state.step === 'loading'">
                <p>
                  <MedSpinner size="3xl" :data-test="getTestId('athina-import-export-spinner')" />
                  Import wird verarbeitet...
                </p>
              </div>

              <div v-else-if="state.step === 'success'">
                <ul class="md-list">
                  <li
                    v-for="(log, index) in state.importLog"
                    :key="index"
                    class="mt-2"
                    :class="{'ml-6 text-sm text-gray-600': log.type === 'small'}"
                    v-text="log.text"
                  />
                </ul>
              </div>

              <div v-else-if="state.step === 'error'">
                <ComponentAlert type="error" :test-id="getTestId('athina-dialog-import')">
                  Beim Importieren ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
                </ComponentAlert>
              </div>
            </div>
          </ComponentTab>

          <ComponentTab title="Export" :test-id="getTestId('athina-dialog-export')">
            <div>
              <h3 class="py-3 text-lg leading-6 font-medium text-gray-700">ATHINA Export</h3>

              <div class="flex flex-col gap-3">
                <p>
                  Exportiert die aktuelle Medikation passend zum Einfügen in den
                  <!-- prettier-ignore -->
                  <span><strong><span style="color: red">A</span>THINA</strong>-Bogen</span>
                  in die Zwischenablage. Im
                  <!-- prettier-ignore -->
                  <span><strong><span style="color: red">A</span>THINA</strong>-Bogen</span>
                  werden die Spalten „PZN“ bis „Behandlungsgrund“ befüllt.
                </p>
                <p>
                  Öffnen Sie nach dem Klick auf „Kopieren“ im MediCheck die
                  <!-- prettier-ignore -->
                  <strong><span style="color: red">A</span>THINA</strong>
                  Excel-Datei und klicken Sie in die PZN-Spalte der obersten Zeile der Medikation.
                </p>
                <img
                  src="data:image/png;base64,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"
                  alt="Erste Zelle im ATHINA-Excelbogen ist markiert"
                />
                <p>
                  Wählen Sie dann in Excel „Einfügen“ bzw. drücken Sie
                  <code>[Strg]+[v]</code>
                  zum Einfügen der aus dem MediCheck exportieren Medikation.
                </p>

                <textarea
                  ref="refExportPayloadTextarea"
                  v-model="state.exportPayload"
                  aria-label="Export-Zwischenablage"
                  style="height: 0; width: 0; opacity: 0"
                />
              </div>
            </div>
          </ComponentTab>
        </ComponentTabs>
      </template>

      <template #actions>
        <template v-if="state.activeTab === 'Import'">
          <template v-if="state.step === 'success'">
            <MedButton
              variant="primary"
              :data-test="getTestId('athina-dialog-import-close-button')"
              @click="closeDialog"
            >
              Schließen
            </MedButton>

            <MedButton :data-test="getTestId('athina-dialog-import-reset-button')" @click="reset">
              Erneut importieren
            </MedButton>
          </template>

          <template v-else>
            <template v-if="state.step === 'start'">
              <MedButton
                variant="primary"
                icon="content_paste"
                :data-test="getTestId('athina-dialog-start-button')"
                @click="importFromAthina"
              >
                Einfügen
              </MedButton>
            </template>

            <template v-else-if="state.step === 'error'">
              <MedButton
                variant="primary"
                icon="content_paste"
                :data-test="getTestId('athina-dialog-reset-button')"
                @click="reset"
              >
                Erneut versuchen
              </MedButton>
            </template>

            <MedButton :data-test="getTestId('athina-dialog-close-button')" @click="closeDialog">Abbrechen</MedButton>
          </template>
        </template>

        <div v-if="state.activeTab === 'Export'" class="flex gap-4">
          <MedButton :data-test="getTestId('athina-dialog-export-cancel-button')" @click="closeDialog">
            Abbrechen
          </MedButton>

          <MedButton
            :variant="state.exportPayload ? 'secondary' : 'primary'"
            :icon="state.exportPayload ? 'check' : 'content_copy'"
            :icon-filled="false"
            :data-test="getTestId('athina-dialog-copy-start-button')"
            @click="exportToAthina"
          >
            Kopieren
          </MedButton>
        </div>
      </template>
    </ComponentDialog>

    <MedButton
      variant="primary"
      flat
      icon="content_paste"
      :disabled="disabled"
      :data-test="getTestId('athina-open-dialog-button')"
      @click="openDialog"
    >
      {{ label }}
    </MedButton>
  </div>
</template>
